import { Home, Person, Portrait } from "@mui/icons-material";
import { Box, Button, Checkbox, Chip, Divider, Grid, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useProfile } from "../../../context/ProfileContext";
import { HtmlTooltip } from "../../../infra/components/tooltipsCustom/tooltipsCuston";
import InfoItemWithCopy from "../../../shared/components/InfoItemWithCopy/InfoItemWithCopy";
import { LoadingComp } from "../../../shared/components/loading/Loading";
import { ToolbarSinister } from "../../../shared/components/toolbarSinister/toolbarSinister";
import { PermissionActionsEnum, PermissionRolesEnum } from "../../../shared/enums/profiles";
import { checkByProfileGuard } from "../../../shared/guards/ProfileGuard";
import { LayoutBaseDePagina } from "../../../shared/layouts";
import { IAgreement, SinisterAgreementService } from "../../../shared/services/api/sinister/SinisterAgreementService";
import { globalMaskDocument } from "../../../shared/utils";
import { formatDateFnsRemoveTimezone } from "../../../shared/utils/format-date";
import { formatScaleValueBR } from "../../../shared/utils/formatScaleValue";
import { getAgreementStatusChipColors, getAgreementStatusLabel } from "../constants/agreementStatusOptions";
import { getInstallmentStatusChipColors, getInstallmentStatusLabel } from "../constants/installmentStatusOptions";
import { useAgreementNegotiation } from "../hooks/useAgreementNegotiation";
import { AgreementNegotiationHistorySection } from "./AgreementNegotiationHistorySection";
import { AgreementNegotiationSection } from "./AgreementNegotiationSection";
import { InstallmentBulkActionsMenu } from "./InstallmentBulkActionsMenu";
import { InstallmentRowMenu } from "./InstallmentRowMenu";
import { ModalCancelAgreement } from "./Modals/ModalCancelAgreement";
import { ModalDefineAgencyTransferPlan } from "./Modals/ModalDefineAgencyTransferPlan";
import { ModalRenegotiateAgreement } from "./Modals/ModalRenegotiateAgreement";
import { ModalUpdateAgreementStatus } from "./Modals/ModalUpdateAgreementStatus";

const sectionTitleSx = { mb: 1.5, textTransform: "uppercase" as const };

export const AgreementsDetailsPresentation = () => {
	const { id = "" } = useParams<"id">();
	const navigate = useNavigate();
	const [agreement, setAgreement] = useState<IAgreement | null>(null);
	const [isLoading, setIsLoading] = useState(true);
	const [openStatusModal, setOpenStatusModal] = useState(false);
	const [openRenegotiateModal, setOpenRenegotiateModal] = useState(false);
	const [openCancelModal, setOpenCancelModal] = useState(false);
	const [openDefineTransferModal, setOpenDefineTransferModal] = useState(false);
	const [selectedInstallmentNumbers, setSelectedInstallmentNumbers] = useState<Set<number>>(new Set());
	const { isSuperAdmin } = useProfile();
	const isLegal = agreement?.sector === "juridico";
	const canUpdateStatus =
		isSuperAdmin ||
		checkByProfileGuard(
			PermissionRolesEnum.AgreementsOptions,
			isLegal ? PermissionActionsEnum.AlterAgreementStatusLegal : PermissionActionsEnum.AlterAgreementStatusCollection
		);
	const isAlreadyRenegotiated = agreement?.status === "renegotiated";
	const canRenegotiate =
		(isSuperAdmin ||
			checkByProfileGuard(
				PermissionRolesEnum.AgreementsOptions,
				isLegal ? PermissionActionsEnum.RenegotiateAgreementLegal : PermissionActionsEnum.RenegotiateAgreementCollection
			)) &&
		!isAlreadyRenegotiated;
	const isAgreementCanceled = agreement?.status === "broken";
	const canDefineTransferPlan =
		(isSuperAdmin ||
			checkByProfileGuard(
				PermissionRolesEnum.AgreementsOptions,
				isLegal ? PermissionActionsEnum.DefineAgencyTransferPlanLegal : PermissionActionsEnum.DefineAgencyTransferPlanCollection
			)) &&
		!isAgreementCanceled;
	const hasExistingTransferPlan =
		agreement?.installments.some((installment) => (installment.agencyTransferValue ?? 0) > 0 || !!installment.agencyTransferRegistration) ?? false;
	const {
		negotiation,
		balance: negotiationBalance,
		history: negotiationHistory,
		isLoading: isLoadingNegotiation,
		refetch: refetchNegotiation,
	} = useAgreementNegotiation(agreement?.sinisterId ?? "");

	const fetchAgreement = () => {
		setIsLoading(true);
		SinisterAgreementService.getById(id).then((result) => {
			if (!(result instanceof Error)) setAgreement(result);
			setIsLoading(false);
		});
	};

	useEffect(() => {
		if (id) fetchAgreement();
	}, [id]);

	if (!agreement) {
		return (
			<LayoutBaseDePagina titulo="Acordos" subtitulo="Detalhes do acordo">
				<LoadingComp isopen={isLoading} />
			</LayoutBaseDePagina>
		);
	}

	const statusColors = getAgreementStatusChipColors(agreement.status);
	const selectedInstallments = agreement.installments.filter((installment) => selectedInstallmentNumbers.has(installment.number));
	const areAllInstallmentsSelected = agreement.installments.length > 0 && selectedInstallmentNumbers.size === agreement.installments.length;
	const areSomeInstallmentsSelected = selectedInstallmentNumbers.size > 0 && !areAllInstallmentsSelected;

	const toggleInstallmentSelection = (installmentNumber: number) => {
		setSelectedInstallmentNumbers((prev) => {
			const next = new Set(prev);
			if (next.has(installmentNumber)) next.delete(installmentNumber);
			else next.add(installmentNumber);
			return next;
		});
	};

	const toggleAllInstallmentsSelection = () => {
		setSelectedInstallmentNumbers((prev) => (prev.size === agreement.installments.length ? new Set() : new Set(agreement.installments.map((i) => i.number))));
	};

	const handleBulkActionsConfirmed = () => {
		setSelectedInstallmentNumbers(new Set());
		fetchAgreement();
		refetchNegotiation();
	};

	return (
		<LayoutBaseDePagina
			titulo="Acordos"
			subtitulo={`Espelho do acordo [ ${agreement.proposal.name} - ${globalMaskDocument(agreement.proposal.document)} ]`}
			barraDeFerramentas={
				<ToolbarSinister
					clickButtonBack={() => navigate("/sinistros/acordos")}
					showButtonBackLoading={isLoading}
					showButtonDocument={false}
					showButtonValues={false}
					showButtonResponsible={false}
					showButtonFinish={false}
					showButtonFollowUp={false}
					showButtonValid={canUpdateStatus}
					textButtonValid="Alterar Status"
					clickButtonValid={() => setOpenStatusModal(true)}
					showButtonConfirm={canRenegotiate}
					textButtonConfirm="Renegociar"
					clickButtonConfirm={() => setOpenRenegotiateModal(true)}
					showButtonCancel={canUpdateStatus}
					textButtonCancel="Cancelar Acordo"
					clickButtonCancel={() => setOpenCancelModal(true)}
				/>
			}
		>
			<Box
				sx={{
					width: "96%",
					maxWidth: "1080px",
					ml: 1,
					p: { xs: 2, sm: 3 },
					bgcolor: "background.paper",
					borderRadius: 2,
					border: "1px solid",
					borderColor: "divider",
					boxShadow: "0 1px 4px rgba(0,0,0,0.05)",
				}}
				component="div"
			>
				<Stack spacing={3}>
					<Box display="flex" justifyContent="space-between" alignItems="flex-start">
						<Box flex={1}>
							<Typography variant="h6" sx={{ color: "#4c4c4d", fontWeight: 500, mb: 1.5 }}>
								Locatário
							</Typography>
							<Grid container spacing={2}>
								<Grid item xs={12} sm={4}>
									<InfoItemWithCopy label="Nome" value={agreement.proposal.name} icon={<Person />} />
								</Grid>
								<Grid item xs={12} sm={4}>
									<InfoItemWithCopy label="Documento" value={agreement.proposal.document} icon={<Portrait />} valueFormatter={globalMaskDocument} />
								</Grid>
								<Grid item xs={12} sm={4}>
									<InfoItemWithCopy label="Imobiliária" value={agreement.client.name} icon={<Home />} />
								</Grid>
							</Grid>
						</Box>

						<Chip
							size="medium"
							label={getAgreementStatusLabel(agreement.status)}
							sx={{ ml: 2, flexShrink: 0, fontWeight: 500, bgcolor: statusColors.bg, color: statusColors.color, border: "0.5px solid #f1f1f1" }}
						/>
					</Box>

					{agreement.status === "broken" && agreement.cancelReason && (
						<Box>
							<Typography variant="h6" sx={{ color: "#4c4c4d", fontWeight: 500, mb: 1.5 }}>
								Quebra de Acordo
							</Typography>
							<Box sx={{ width: "98%", backgroundColor: "rgba(255, 0, 0, 0.05)", padding: 2, borderRadius: 1, boxSizing: "border-box" }}>
								<Typography variant="body1" color="error">
									Motivo: {agreement.cancelReason}
								</Typography>
							</Box>
						</Box>
					)}

					<AgreementNegotiationSection agreement={agreement} negotiation={negotiation} balance={negotiationBalance} isLoading={isLoadingNegotiation} />

					<Divider />

					<Box>
						<Typography variant="subtitle2" color="primary" fontWeight={700} sx={sectionTitleSx}>
							Débitos Incluídos
						</Typography>
						<TableContainer sx={{ maxHeight: 320 }}>
							<Table size="small" stickyHeader>
								<TableHead>
									<TableRow>
										<TableCell>Descrição</TableCell>
										<TableCell>Referência</TableCell>
										<TableCell align="center">Vencimento</TableCell>
										<TableCell align="right">Valor</TableCell>
									</TableRow>
								</TableHead>
								<TableBody>
									{agreement.values.map((value) => (
										<TableRow key={value.id}>
											<TableCell>{value.description}</TableCell>
											<TableCell>{value.reference}</TableCell>
											<TableCell align="center">{formatDateFnsRemoveTimezone(value.dueDate) || "-"}</TableCell>
											<TableCell align="right">{formatScaleValueBR(String(value.value))}</TableCell>
										</TableRow>
									))}
								</TableBody>
							</Table>
						</TableContainer>
					</Box>

					<Divider />

					<Box>
						<Grid container justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
							<Typography variant="subtitle2" color="primary" fontWeight={700} sx={{ textTransform: "uppercase" }}>
								Parcelas de Pagamento
							</Typography>
							<Stack direction="row" spacing={1}>
								<InstallmentBulkActionsMenu agreement={agreement} installments={selectedInstallments} onChanged={handleBulkActionsConfirmed} />
								<Button
									size="small"
									variant="outlined"
									disabled={!canDefineTransferPlan}
									onClick={() => setOpenDefineTransferModal(true)}
								>
									{hasExistingTransferPlan ? "Redefinir Repasse à Imobiliária" : "Definir Repasse à Imobiliária"}
								</Button>
							</Stack>
						</Grid>
						<TableContainer sx={{ maxHeight: 320 }}>
							<Table size="small" stickyHeader>
								<TableHead>
									<TableRow>
										<TableCell padding="checkbox">
											<Checkbox
												size="small"
												checked={areAllInstallmentsSelected}
												indeterminate={areSomeInstallmentsSelected}
												onChange={toggleAllInstallmentsSelection}
												inputProps={{ "aria-label": "Selecionar todas as parcelas" }}
											/>
										</TableCell>
										<TableCell>Parcela</TableCell>
										<TableCell align="center">Vencimento</TableCell>
										<TableCell align="right">Valor</TableCell>
										<TableCell align="center">Status</TableCell>
										<TableCell align="center">Repasse Imobiliária</TableCell>
										<TableCell align="center">Ações</TableCell>
									</TableRow>
								</TableHead>
								<TableBody>
									{agreement.installments.map((installment) => {
										const installmentColors = getInstallmentStatusChipColors(installment.status);
										const hasPlannedTransfer = (installment.agencyTransferValue ?? 0) > 0;
										const isTransferRegistered = !!installment.agencyTransferRegistration;
										return (
											<TableRow key={installment.number} selected={selectedInstallmentNumbers.has(installment.number)}>
												<TableCell padding="checkbox">
													<Checkbox
														size="small"
														checked={selectedInstallmentNumbers.has(installment.number)}
														onChange={() => toggleInstallmentSelection(installment.number)}
														inputProps={{ "aria-label": `Selecionar parcela ${installment.number}` }}
													/>
												</TableCell>
												<TableCell>
													{installment.number}
													{!!installment.reissueCount && (
														<Typography component="span" variant="caption" color="text.secondary" ml={0.5}>
															({installment.reissueCount + 1}ª via)
														</Typography>
													)}
												</TableCell>
												<TableCell align="center">{formatDateFnsRemoveTimezone(installment.dueDate) || "-"}</TableCell>
												<TableCell align="right">{formatScaleValueBR(String(installment.value))}</TableCell>
												<TableCell align="center">
													<Chip
														size="small"
														label={getInstallmentStatusLabel(installment.status)}
														sx={{ bgcolor: installmentColors.bg, color: installmentColors.color, fontWeight: 500 }}
													/>
												</TableCell>
												<TableCell align="center">
													{hasPlannedTransfer || isTransferRegistered ? (
														<Stack spacing={0.3} alignItems="center">
															{hasPlannedTransfer && (
																<Typography variant="body2" fontWeight={500}>
																	{formatScaleValueBR(String(installment.agencyTransferValue))}
																</Typography>
															)}
															{isTransferRegistered ? (
																<HtmlTooltip
																	title={
																		<Box>
																			<Typography variant="caption" fontWeight="bold" display="block">
																				Repasse confirmado
																			</Typography>
																			<Typography variant="caption" display="block">
																				Data: {formatDateFnsRemoveTimezone(installment.agencyTransferRegistration!.transferDate) || "-"}
																			</Typography>
																			<Typography variant="caption" display="block">
																				Valor: {formatScaleValueBR(String(installment.agencyTransferRegistration!.value))}
																			</Typography>
																		</Box>
																	}
																	arrow
																	placement="top"
																>
																	<Typography variant="caption" color="success.main" fontWeight={600} sx={{ cursor: "default" }}>
																		Repassado: {formatScaleValueBR(String(installment.agencyTransferRegistration!.value))}
																	</Typography>
																</HtmlTooltip>
															) : (
																<Chip size="small" label="Pendente" color="warning" variant="outlined" />
															)}
														</Stack>
													) : (
														<Typography variant="body2" color="text.secondary">
															—
														</Typography>
													)}
												</TableCell>
												<TableCell align="center">
													<InstallmentRowMenu
														agreement={agreement}
														installment={installment}
														onChanged={() => {
															fetchAgreement();
															refetchNegotiation();
														}}
													/>
												</TableCell>
											</TableRow>
										);
									})}
								</TableBody>
							</Table>
						</TableContainer>
					</Box>

					<AgreementNegotiationHistorySection history={negotiationHistory} />
				</Stack>
			</Box>

			<LoadingComp isopen={isLoadingNegotiation} />

			<ModalUpdateAgreementStatus
				open={openStatusModal}
				agreement={agreement}
				onClose={() => setOpenStatusModal(false)}
				onConfirm={() => {
					setOpenStatusModal(false);
					fetchAgreement();
					refetchNegotiation();
				}}
			/>

			<ModalRenegotiateAgreement
				open={openRenegotiateModal}
				agreement={agreement}
				balance={negotiationBalance}
				onClose={() => setOpenRenegotiateModal(false)}
				onConfirm={() => {
					setOpenRenegotiateModal(false);
					fetchAgreement();
					refetchNegotiation();
				}}
			/>

			<ModalCancelAgreement
				open={openCancelModal}
				agreement={agreement}
				onClose={() => setOpenCancelModal(false)}
				onConfirm={() => {
					setOpenCancelModal(false);
					fetchAgreement();
					refetchNegotiation();
				}}
			/>

			<ModalDefineAgencyTransferPlan
				open={openDefineTransferModal}
				agreementId={agreement.id}
				onClose={() => setOpenDefineTransferModal(false)}
				onConfirm={() => {
					setOpenDefineTransferModal(false);
					fetchAgreement();
					refetchNegotiation();
				}}
			/>
		</LayoutBaseDePagina>
	);
};
